import { z } from "zod";
import { defineEvent } from "./registry.js";
/**
 * İstasyon ajanı olayları (Prompt 14).
 *
 * **Tek tek senkron olayı için outbox kaydı YOKTUR**: bir ajan günde on binlerce olay gönderir. Parti başına
 * BİR özet olayı yazılır (`agent.sync.batch_received`); ayrıntıyı isteyen istemci yetki denetimli uçlardan çeker.
 *
 * Yük KİŞİSEL VERİ TAŞIMAZ; açık anahtar, kayıt kodu ve token hiçbir olayda yer almaz (KVKK, AGENTS.md §3.14).
 * Zarf `metadata.stationId` taşır → WebSocket istasyon odası.
 */
const agentBase = {
    agentId: z.uuid(),
    companyId: z.uuid(),
    stationId: z.uuid(),
};
export const agentEnrolledV1 = defineEvent({
    type: "agents.agent.enrolled",
    version: 1,
    description: "Yeni istasyon ajanı kayıt kodu ile kaydoldu",
    realtime: ["tenant", "station"],
    payload: z.object({
        ...agentBase,
        hostname: z.string(),
        os: z.string(),
        arch: z.string(),
        version: z.string(),
    }),
});
export const agentRevokedV1 = defineEvent({
    type: "agents.agent.revoked",
    version: 1,
    description: "Ajan iptal edildi: token yenileyemez, senkron ve komut uçları 401 döner",
    realtime: ["tenant", "station"],
    payload: z.object({
        ...agentBase,
        hostname: z.string(),
        /** İptal gerekçesi iş bilgisidir (kişisel veri değildir); denetim izinde de vardır. */
        reason: z.string(),
    }),
});
export const agentOnlineV1 = defineEvent({
    type: "agents.agent.online",
    version: 1,
    description: "Ajan çevrimiçi oldu (çevrimdışıyken gelen ilk heartbeat)",
    realtime: ["tenant", "station"],
    payload: z.object({
        ...agentBase,
        version: z.string(),
        offlineSeconds: z.number().int().nonnegative().nullable(),
    }),
});
export const agentOfflineV1 = defineEvent({
    type: "agents.agent.offline",
    version: 1,
    description: "Ajandan beklenen süre içinde heartbeat gelmedi (dönem başına bir kez)",
    realtime: ["tenant", "station"],
    payload: z.object({
        ...agentBase,
        hostname: z.string(),
        lastSeenAt: z.iso.datetime({ offset: true }).nullable(),
        thresholdSeconds: z.number().int().positive(),
    }),
});
export const agentClockSkewDetectedV1 = defineEvent({
    type: "agents.agent.clock_skew_detected",
    version: 1,
    description: "Ajanın saati sunucudan eşiğin üzerinde sapıyor (sapma dönemi başına bir kez)",
    realtime: ["tenant", "station"],
    payload: z.object({
        ...agentBase,
        clockSkewMs: z.number().int(),
        thresholdSeconds: z.number().int().positive(),
    }),
});
export const agentDeviceAlarmRaisedV1 = defineEvent({
    type: "agents.device.alarm_raised",
    version: 1,
    description: "Ajan bir cihaz alarmı bildirdi (pompa, tank probu, ÖKC…)",
    realtime: ["tenant", "station"],
    payload: z.object({
        ...agentBase,
        alarmId: z.uuid(),
        adapterId: z.string(),
        deviceRef: z.string(),
        code: z.string(),
        severity: z.enum(["INFO", "WARNING", "CRITICAL"]),
        message: z.string(),
    }),
});
export const agentCommandCreatedV1 = defineEvent({
    type: "agents.command.created",
    version: 1,
    description: "Ajana komut kuyruğa alındı (elle ya da olay tüketicisi tarafından)",
    realtime: ["tenant", "station"],
    payload: z.object({
        ...agentBase,
        commandId: z.uuid(),
        type: z.string(),
        /** true → komut bir olaydan türetildi (fiyat değişikliği gibi), kullanıcı göndermedi. */
        system: z.boolean(),
        expiresAt: z.iso.datetime({ offset: true }),
    }),
});
export const agentCommandFinishedV1 = defineEvent({
    type: "agents.command.finished",
    version: 1,
    description: "Komut sonuçlandı (SUCCEEDED / FAILED / EXPIRED / CANCELLED)",
    realtime: ["tenant", "station"],
    payload: z.object({
        ...agentBase,
        commandId: z.uuid(),
        type: z.string(),
        status: z.enum(["SUCCEEDED", "FAILED", "EXPIRED", "CANCELLED"]),
        attempt: z.number().int().nonnegative(),
        detail: z.string().nullable(),
    }),
});
export const agentCommandPriceUpdateFailedV1 = defineEvent({
    type: "agents.command.price_update_failed",
    version: 1,
    description: "Fiyat güncelleme komutu başarısız oldu ya da süresi doldu — pompa ESKİ fiyattan satıyor olabilir",
    realtime: ["tenant", "station"],
    payload: z.object({
        ...agentBase,
        commandId: z.uuid(),
        status: z.enum(["FAILED", "EXPIRED"]),
        requestNo: z.string().nullable(),
        effectiveAt: z.iso.datetime({ offset: true }).nullable(),
        detail: z.string().nullable(),
    }),
});
export const agentSyncGapSkippedV1 = defineEvent({
    type: "agents.sync.gap_skipped",
    version: 1,
    description: "Sunucu yedekten döndükten sonra ajanın artık saklamadığı olaylar yeniden gönderilemedi; sıra aralığı atlandı (veri kaybı)",
    realtime: ["tenant", "station"],
    payload: z.object({
        ...agentBase,
        fromSeq: z.number().int().positive(),
        toSeq: z.number().int().positive(),
        lostCount: z.number().int().positive(),
    }),
});
export const agentSyncBatchReceivedV1 = defineEvent({
    type: "agents.sync.batch_received",
    version: 1,
    description: "Ajan senkron partisi işlendi (kabul / yinelenen / reddedilen sayıları)",
    realtime: ["station"],
    payload: z.object({
        ...agentBase,
        batchId: z.uuid(),
        received: z.number().int().nonnegative(),
        accepted: z.number().int().nonnegative(),
        duplicates: z.number().int().nonnegative(),
        rejected: z.number().int().nonnegative(),
        ackedSeq: z.number().int().nonnegative(),
        /** Reddedilen kayıtların hata kodları ve sayıları — alarm / izleme için (kayıt ayrıntısı YOK). */
        rejectedCodes: z.record(z.string(), z.number().int().positive()),
        /** İşlenen olay türleri ve sayıları. */
        typeCounts: z.record(z.string(), z.number().int().positive()),
    }),
});
//# sourceMappingURL=agents.v1.js.map